import styled from '@emotion/styled';
import { Badge, Checkbox, Tag } from 'antd';
import React from 'react';
import { StoreInfo, StoreNameConfig } from 'services/api/store';
import { ReactComponent as IconFollow } from 'components/StoreSelector/assets/icon_concern.svg';
import { ReactComponent as IconFollowed } from 'components/StoreSelector/assets/icon_concern_done.svg';
import Icon from '@ant-design/icons';
import Tooltip from 'antd/es/tooltip';
import { useTranslate } from '@tolgee/react';
import { isStoreAliasFirst } from 'hooks/business/store/useStoreName';
import Highlighter from 'react-highlight-words';
import { css } from '@emotion/css';
import {
  useAuthBeAboutExpireShopMapValue,
  useAuthInvalidShopMapValue,
} from 'hooks/business/authShops/useAuthInvalidShops';
import QuickSelectButton from '../QuickSelectButton';
import { AuthBeAboutToExpireFeature } from 'utils/featureFlag';
import dayjs from 'lib/dayjs';
import { useAnySuperUser } from 'hooks/useAnySuperUser';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { CountryFlagIcon } from 'components/CountryFlagIcon';
import { useGetRegionLabel } from 'hooks/business/useRegion';

interface Props {
  item: StoreInfo;
  onSelect: (item: StoreInfo[], checked: boolean) => void;
  onSubmit?: (item: StoreInfo[]) => void;
  isSelected: (storeId: string) => boolean;
  isFollowed: (storeId: string) => boolean;
  onFollowChange: (item: StoreInfo, status: boolean) => void;
  storeNameConfig?: StoreNameConfig[];
  highlightWords?: string;
  displayUnreadCount?: boolean;
}

const Item = styled.div`
  cursor: pointer;
  height: 40px;
  padding: 0 8px;
  margin: 0 8px;
  .info {
    display: flex;
    height: 100%;
    gap: 6px;
    width: 100%;
    overflow: hidden;
    align-items: center;
    .store-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .unread-count {
      width: 38px;
      text-align: right;
    }
  }
  .action {
    visibility: hidden;
    display: flex;
    align-items: center;
    svg {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }
  &:hover {
    background-color: #f7f9fa;
    .action {
      visibility: visible;
    }
  }
  .highlight-words {
    color: #336fff;
    background-color: unset;
    padding: 0;
  }
`;
const storeNameTooltipCls = css`
  max-width: 200px;
  div {
    word-break: break-all;
  }
`;
const toolTipCls = css`
  max-width: 200px !important;
`;

const StoreListItem = React.memo((props: Props) => {
  const {
    item,
    onFollowChange,
    onSelect,
    isSelected,
    isFollowed,
    onSubmit,
    storeNameConfig,
    highlightWords,
    displayUnreadCount = true,
  } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const name =
    isStoreAliasFirst(storeNameConfig, item.externalType, item.region) && item.alias
      ? item.alias
      : item.storeName || '';

  const { externalType, region, storeId } = item;

  const authInvalidShopMap = useAuthInvalidShopMapValue();
  const authBeAboutExpireShopMap = useAuthBeAboutExpireShopMapValue();

  const { [externalType]: { [region]: { [storeId]: authInvalidStore } = {} } = {} } =
    authInvalidShopMap;

  const { [externalType]: { [region]: { [storeId]: authBeAboutStore } = {} } = {} } =
    authBeAboutExpireShopMap;

  return (
    <Item>
      <div className="info" onClick={() => onSelect([item], !isSelected(item.storeId))}>
        <Checkbox checked={isSelected(item.storeId)} />
        {item.region && item.externalType !== 'FACEBOOK' && (
          <Tooltip
            mouseEnterDelay={0.5}
            title={`${item.externalType?.[0] || ''}${item.externalType
              ?.slice(1)
              ?.toLowerCase?.()} ${getRegionLabel(item.region)}`}
          >
            <span>
              <CountryFlagIcon region={item.region} />
            </span>
          </Tooltip>
        )}

        <Tooltip
          className={toolTipCls}
          mouseEnterDelay={0.5}
          title={
            <div className={storeNameTooltipCls}>
              <div>{item.storeName}</div>
              <div>{item.alias}</div>
            </div>
          }
        >
          <Highlighter
            autoEscape
            textToHighlight={name}
            searchWords={[highlightWords || '']}
            highlightClassName="highlight-words"
            className="store-name"
          />
        </Tooltip>
        <div className="action">
          <Tooltip
            mouseEnterDelay={0.5}
            title={
              isFollowed(item.storeId) ? t('storeSelector.取消关注') : t('storeSelector.关注店铺')
            }
          >
            <Icon
              component={isFollowed(item.storeId) ? IconFollowed : IconFollow}
              size={18}
              onClick={(e) => {
                e.stopPropagation();
                onFollowChange(item, !isFollowed(item.storeId));
              }}
            />
          </Tooltip>
          <QuickSelectButton onClick={() => onSubmit?.([item])} />
        </div>
        {displayUnreadCount && (
          <div className="unread-count">
            <Badge count={item.unreadCount} />
          </div>
        )}
        {authInvalidStore ? (
          <Tooltip
            title={t('storeSelector.当前店铺收发消息、订单同步异常，请尽快联系管理重新添加店铺')}
          >
            <Tag>{t('storeSelector.已失效')}</Tag>
          </Tooltip>
        ) : null}

        {AuthBeAboutToExpireFeature && authBeAboutStore ? (
          <Tooltip
            title={
              <StoreItemBeAboutToExpireTooltip
                storeId={authBeAboutStore.id}
                day={dayjs(authBeAboutStore.expireTimeStamp).diff(dayjs(), 'days')}
              />
            }
          >
            <Tag className="!bg-[#fff]">{t('be_about_to_expire')}</Tag>
          </Tooltip>
        ) : null}
      </div>
    </Item>
  );
});

const StoreItemBeAboutToExpireTooltip = ({ storeId, day }: { storeId: number; day: number }) => {
  const { t } = useTranslate();
  const hasPermission = useGetPermission()('店铺管理');
  const name = useAnySuperUser(storeId);
  return (
    <>
      {hasPermission
        ? t('perm_store_item_be_about_expire_tip', {
            day,
          })
        : t('no_perm_store_item_be_about_expire_tip', {
            day,
            name,
          })}
    </>
  );
};

export default StoreListItem;
